import PropTypes from 'prop-types';
import React, { FC, Fragment, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ValidatedAutosuggest, {
  renderSuggestion,
} from '../Inputs/ValidatedAutosuggest';

export const getFilterESParams = (excludeList, objectFilters) => {
  if (!(objectFilters?.length > 0)) {
    // return undefined instead of null to ensure no param is passed in
    return undefined;
  }

  return {
    activities: excludeList.includes('activities')
      ? undefined
      : objectFilters.filter((f) => f._index === 'activities').map((f) => f.id),
    skills: excludeList.includes('skills')
      ? undefined
      : objectFilters.filter((f) => f._index === 'skills').map((f) => f.id),
    credentials: excludeList.includes('credentials')
      ? undefined
      : objectFilters
          .filter((f) => f._index === 'credentials')
          .map((f) => f.id),
    people: excludeList.includes('people')
      ? undefined
      : objectFilters.filter((f) => f._index === 'people').map((f) => f.id),
  };
};

const FilterInput: FC<Props> = ({ autoFocus = false, ...props }) => {
  const propsSetObjectFilters = props.setObjectFilters;
  const propsObjectFilters = props.objectFilters;
  const propsOnChange = props.onChange;
  const intl = useIntl();

  const onChangeObject = useCallback(
    (object) => {
      // @ts-expect-error
      propsSetObjectFilters([...propsObjectFilters, object]);

      // clear input
      propsOnChange({
        target: {
          name: 'q',
          value: '',
        },
      });
    },
    [propsSetObjectFilters, propsObjectFilters, propsOnChange]
  );

  const onRemoveObjectFilter = useCallback(
    (o) => {
      // @ts-expect-error
      propsSetObjectFilters(propsObjectFilters.filter((o2) => o.id !== o2.id));
    },
    [propsSetObjectFilters, propsObjectFilters]
  );

  const renderObject = useCallback(
    (o) => {
      return renderSuggestion(
        intl,
        o,
        '',
        true,
        'xxs',
        false,
        // @ts-expect-error
        'Remove filter',
        () => onRemoveObjectFilter(o),
        true,
        false,
        false
        //validationFunction
      );
    },
    [intl, onRemoveObjectFilter]
  );

  const activityFilters = props.objectFilters?.filter(
    // @ts-expect-error
    (f) => f._index === 'activities'
  );
  const personFilters = props.objectFilters?.filter(
    // @ts-expect-error
    (f) => f._index === 'people'
  );
  const skillFilters = props.objectFilters?.filter(
    // @ts-expect-error
    (f) => f._index === 'skills'
  );
  const credentialFilters = props.objectFilters?.filter(
    // @ts-expect-error
    (f) => f._index === 'credentials'
  );

  const spanClasses = 'd-inline-block text-muted mt-2 me-2';

  const placeholder =
    // @ts-expect-error
    props.placeholder ??
    intl.formatMessage({
      id: 'app.widgets.search.filter_input.placeholder',
      defaultMessage: 'Filter by name',
      description: 'Placeholder for the filter input',
    });

  return (
    <>
      <ValidatedAutosuggest
        size="sm"
        className="w-100 mb-4"
        iconClassName="fe fe-search"
        placeholder={placeholder}
        name="q"
        value={props.value}
        onInputChange={props.onChange}
        onSuggestionSelected={onChangeObject}
        autoFocus={autoFocus}
        suggestions={props.suggestions}
        clearInputOnSelect={true}
        dropdownMenuClass={'dropdown-menu-card'}
        elasticsearchOptions={
          props.fetchObjectsFromElasticsearch
            ? {
                url: 'get-all-by-name',
                getQuery: (q) => ({
                  exclude: props.excludeIndices,
                  name: q,
                  sort: [
                    {
                      status: {
                        unmapped_type: 'keyword',
                        order: 'asc',
                      },
                      'full_name.raw': {
                        unmapped_type: 'keyword',
                        order: 'asc',
                      },
                    },
                  ],
                }),
              }
            : undefined
        }
      />
      {/* @ts-expect-error */}
      {props.objectFilters?.length > 0 && (
        <>
          <span className="d-inline-block text-muted mt-2">
            <FormattedMessage
              id="app.widgets.search.filter_input.showing"
              defaultMessage="Showing {typeDisplayName}"
              description="Prefix for the filter input result display"
              values={{ typeDisplayName: props.typeDisplayName }}
            />
            &nbsp;
          </span>
          {/* @ts-expect-error */}
          {activityFilters?.length > 0 && (
            <>
              <span className={spanClasses}>
                {' '}
                <FormattedMessage
                  id="app.widgets.search.filter_input.for"
                  defaultMessage="for"
                  description="Word before a list of activities"
                />{' '}
              </span>
              {/* @ts-expect-error */}
              {activityFilters.map((o, index) => (
                <Fragment key={index}>{renderObject(o)}</Fragment>
              ))}
            </>
          )}
          {/* @ts-expect-error */}
          {personFilters?.length > 0 && (
            <>
              <span className={spanClasses}>
                {' '}
                <FormattedMessage
                  id="app.widgets.search.filter_input.with"
                  defaultMessage="with"
                  description="Word before a list of people"
                />{' '}
              </span>
              {/* @ts-expect-error */}
              {personFilters.map((o, index) => (
                <Fragment key={index}>{renderObject(o)}</Fragment>
              ))}
            </>
          )}
          {/* @ts-expect-error */}
          {skillFilters?.length > 0 && (
            <>
              <span className={spanClasses}>
                {' '}
                <FormattedMessage
                  id="app.widgets.search.filter_input.using"
                  defaultMessage="using"
                  description="Word before a list of skills"
                />{' '}
              </span>
              {/* @ts-expect-error */}
              {skillFilters.map((o, index) => (
                <Fragment key={index}>{renderObject(o)}</Fragment>
              ))}
            </>
          )}
          {/* @ts-expect-error */}
          {credentialFilters?.length > 0 && (
            <>
              <span className={spanClasses}>
                {' '}
                <FormattedMessage
                  id="app.widgets.search.filter_input.credentialed"
                  defaultMessage="credentialed"
                  description="Word before a list of credentials"
                />{' '}
              </span>
              {/* @ts-expect-error */}
              {credentialFilters.map((o, index) => (
                <Fragment key={index}>{renderObject(o)}</Fragment>
              ))}
            </>
          )}
        </>
      )}
    </>
  );
};

const FilterInput_propTypes = {
  typeDisplayName: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.object),
  // list of indices that can be passed to ElasticSearch as a comma-separated string
  excludeIndices: PropTypes.string,
  objectFilters: PropTypes.arrayOf(PropTypes.object),
  setObjectFilters: PropTypes.func,
  fetchObjectsFromElasticsearch: PropTypes.bool,
};

type Props = PropTypes.InferProps<typeof FilterInput_propTypes>;

// all tracking in app will be passed through here
export default React.memo(FilterInput);
